import * as React from "react";

export interface CheckBoxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  asChild?: boolean;
  label?: string;
  labelPosition?: "top" | "bottom" | "left" | "right";
  alignSelf?: "start" | "center" | "end";
  checkIcon?: React.ReactNode;
  uncheckedIcon?: React.ReactNode;
  className?: string;
  labelClassName?: string;
}

const CheckBox = React.forwardRef<HTMLInputElement, CheckBoxProps>(
  ({ className, label, labelPosition = "right", alignSelf, checkIcon, uncheckedIcon, asChild = false, labelClassName, ...props }, ref) => {
    const [isChecked, setIsChecked] = React.useState(
      props.checked !== undefined ? props.checked : props.defaultChecked || false
    );
    const inputRef = React.useRef<HTMLInputElement>(null);
    const id = props.id || `checkbox-${Math.random().toString(36).substring(2, 11)}`;

    // ref가 함수인 경우와 RefObject인 경우 모두 처리
    const setRef = React.useCallback(
      (node: HTMLInputElement | null) => {
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
        inputRef.current = node;
      },
      [ref]
    );

    // props.checked가 변경될 때 상태 업데이트
    React.useEffect(() => {
      if (props.checked !== undefined) {
        setIsChecked(props.checked);
      }
    }, [props.checked]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsChecked(e.target.checked);
      if (props.onChange) {
        props.onChange(e);
      }
    };

    const alignSelfClass = alignSelf === "start" ? "self-start" : alignSelf === "end" ? "self-end" : "";

    // labelPosition이 top일 때 InputBox와 동일한 레이아웃 사용
    const containerClassName =
      labelPosition === "top"
        ? `relative ${alignSelfClass}`
        : labelPosition === "bottom"
          ? `flex flex-col items-center gap-1 ${alignSelfClass}`
          : `flex items-center gap-1 ${alignSelfClass}`;

    const checkboxElement = (
      <div className="relative w-6 h-6">
        <input
          {...props}
          id={id}
          type="checkbox"
          ref={setRef}
          onChange={handleChange}
          className="absolute w-6 h-6 opacity-0 cursor-pointer z-20"
        />

        <div className="absolute inset-0 pointer-events-none z-10">
          {isChecked
            ? checkIcon || (
                <img src="/images/checkboxActive.svg" alt="checked" width={24} height={24} className="w-6 h-6" />
              )
            : uncheckedIcon || (
                <img src="/images/checkboxInActive.svg" alt="unchecked" width={24} height={24} className="w-6 h-6" />
              )}
        </div>
      </div>
    );

    const labelElement = label && (
      <label
        htmlFor={id}
        className={`cursor-pointer text-base text-[#888] font-normal select-none ${labelClassName}`}
      >
        {label}
      </label>
    );

    // labelPosition이 top일 때 InputBox와 동일한 레이아웃
    if (labelPosition === "top") {
      return (
        <div className={containerClassName}>
          {label && (
            <div className="h-6 mb-1">
              <label htmlFor={id} className="block text-base font-normal text-[#414651] cursor-pointer select-none">
                {label}
              </label>
            </div>
          )}
          {checkboxElement}
        </div>
      );
    }

    return (
      <div className={containerClassName}>
        {labelPosition === "left" && labelElement}
        {checkboxElement}
        {(labelPosition === "bottom" || labelPosition === "right") && labelElement}
      </div>
    );
  }
);

CheckBox.displayName = "CheckBox";

export { CheckBox };
